import { SavedSearchFilters } from "@outschool/gql-backend-generated";
import { onlineClassesPath, searchPath, websiteHost } from "@outschool/routes";

import { CategoryNavLink } from "./navbarCategories";

export function getCategoryUrl(
  category: CategoryNavLink,
  useSearchLinks = false,
): string {
  if (useSearchLinks) {
    return getCategorySearchUrl(category);
  }

  return category.path || onlineClassesPath(category.slug, category.basePath);
}

function getCategorySearchUrl(category: CategoryNavLink): string {
  // Navbar-only CLP paths sometimes carry extra query params (e.g. format=SelfPaced).
  // Hydrated saved-search filters are the source of truth and win on key overlap.
  const paramsFromPath = getQueryParamsFromCategoryPath(category.path);
  const saved = sanitizeSavedSearchFilters(category.searchFilters);
  const query =
    category.searchFilters?.q ?? paramsFromPath.q ?? slugToQuery(category.slug);

  const merged: Record<string, any> = {
    ...paramsFromPath,
    ...saved,
    q: query,
  };

  return searchPath(merged);
}

/**
 * Query string keys from `category.path` (CLP URLs), for params not present on saved search rows.
 * See e.g. navbarCategories.ts and the "applySelfPacedFilter" function.
 */
function getQueryParamsFromCategoryPath(path?: string): Record<string, string> {
  if (!path) {
    return {};
  }

  try {
    const parsedUrl = new URL(path, websiteHost());
    const params: Record<string, string> = {};
    parsedUrl.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  } catch {
    return {};
  }
}

// Same logic as in useCategoryPageRedirect.ts.
function slugToQuery(slug: string): string {
  return slug.replace(/-/g, " ");
}

function sanitizeSavedSearchFilters(
  searchFilters?: SavedSearchFilters | null,
): Record<string, any> {
  if (!searchFilters) {
    return {};
  }

  return Object.entries(searchFilters).reduce((acc, [key, value]) => {
    if (key === "__typename") {
      return acc;
    }
    if (value == null || value === "") {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {} as Record<string, any>);
}
export function hasSubcategories(category: CategoryNavLink): boolean {
  return Boolean(
    category.getSubcategories && category.getSubcategories?.().length > 0,
  );
}
